import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  MFG_STATUS,
  DROPDOWN,
  TITLES,
  ROUTES,
  POPUP_TYPE,
  DATA_FIELD,
  BUTTON,
  MANUFACTURING_ESUN_LIST_COLUMN,
} from '../../../../constants';
import { DropDown, PaginatedTable, SearchBar, TitleBar } from '../../../common';
import { StatusBox } from './StatusBox';
import { GreenTile, RedTile, YellowTile } from './Tiles';
import { vendorLot } from '../../../../service';
import { useDebounce } from '../../../../hooks';
import { popup, manufacturingDataCompiler, reducedCalc } from '../../../../utils';
import { Button } from '../../../shared';

export const ManufacturingContent = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialize state from URL params or default values
  const [mfgData, setMfgData] = useState([]);
  const [sort1, setSort1] = useState(searchParams.get('priority') || 'Priority');
  const [sort2, setSort2] = useState(searchParams.get('qtyStatus') || 'Qty Status');
  const [sort3, setSort3] = useState(searchParams.get('readyStatus') || 'Ready Status');
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const [search, setSearch] = useState(false);
  const [sortingArray1, setSortingArray1] = useState([]);
  const [sortingArray2, setSortingArray2] = useState([]);
  const [sortingArray3] = useState(['Not Ready', 'Partially Ready', 'Fully Ready']);
  const [sortedData, setSortedData] = useState([]);
  const [paginationData, setPaginationData] = useState({});
  const [pageNumber, setPageNumber] = useState(parseInt(searchParams.get('page')) || 1);
  const [jumpPage, setJumpPage] = useState('');
  const [listView, setListView] = useState(searchParams.get('listView') === 'true' || false);
  const [isLoading, setIsLoading] = useState(false);

  // Update URL params when values change
  const updateUrlParams = (params = {}) => {
    const newParams = {};

    // Only include non-empty values
    if (params.search) newParams.search = params.search;
    // Only include filters if they're not the default values
    if (params.priority && params.priority !== 'Priority') newParams.priority = params.priority;
    if (params.qtyStatus && params.qtyStatus !== 'Qty Status')
      newParams.qtyStatus = params.qtyStatus;
    if (params.readyStatus && params.readyStatus !== 'Ready Status')
      newParams.readyStatus = params.readyStatus;
    if (params.page) newParams.page = params.page.toString();
    if (params.listView !== undefined) newParams.listView = params.listView.toString();
    setSearchParams(newParams, { replace: true });
  };

  const getManufacturingList = useDebounce((params = {}) => {
    setIsLoading(true);
    vendorLot({ data: { page_number: params.page_number || pageNumber, ...params } })
      .then((response) => {
        setIsLoading(false);
        const records = response?.data?.records || [];
        if (records.length > 0) {
          const compiledData = manufacturingDataCompiler(records);
          setMfgData(compiledData);
          setPaginationData(response.data.params);

          // Always update the sorting arrays with the latest data from the API
          if (response?.data?.manufacture_priority) {
            setSortingArray1(response.data.manufacture_priority);
          }

          if (response?.data?.qty_status) {
            setSortingArray2([
              ...response.data.qty_status.filter((item) => !DROPDOWN.MFG_SORT_2.includes(item)),
            ]);
          }
        } else {
          setMfgData([]);
        }
      })
      .catch(() => {
        setIsLoading(false);
      });
  }, 400);

  const getList = (additionalParams = {}) => {
    const params = {
      page_number: additionalParams.page_number || pageNumber,
      search: searchTerm
        ? {
          future_lot: searchTerm,
          ['product_id.default_code']: searchTerm,
          ['dyeing_product_id.default_code']: searchTerm,
        }
        : {},
      sort: {
        selection: {
          ...(sort1 !== 'Priority'
            ? { manufacture_priority: sort1?.toString().toLowerCase().replaceAll(' ', '_') }
            : {}),
          ...(sort2 !== 'Qty Status'
            ? {
              qty_status: sort2?.toString().toLowerCase().replaceAll(' ', '_'),
            }
            : {}),
          ...(sort3 !== 'Ready Status'
            ? {
              ready_status: sort3?.toString().toLowerCase().replaceAll(' ', '_'),
            }
            : {}),
        },
      },
      ...additionalParams,
    };

    // Update URL params
    updateUrlParams({
      search: searchTerm,
      priority: sort1,
      qtyStatus: sort2,
      readyStatus: sort3,
      page: params.page_number,
      listView: listView,
    });

    getManufacturingList(params);
  };

  // Initial load - read from URL params
  useEffect(() => {
    setIsLoading(true);
    // On initial load, if we have params in the URL, use them
    const urlSearchTerm = searchParams.get('search');
    const urlPriority = searchParams.get('priority');
    const urlQtyStatus = searchParams.get('qtyStatus');
    const urlReadyStatus = searchParams.get('readyStatus');
    const urlPage = searchParams.get('page');
    const urlListView = searchParams.get('listView');

    let hasChanges = false;

    if (urlSearchTerm && urlSearchTerm !== searchTerm) {
      setSearchTerm(urlSearchTerm);
      hasChanges = true;
    }

    if (urlPriority && urlPriority !== sort1) {
      setSort1(urlPriority);
      hasChanges = true;
    }

    if (urlQtyStatus && urlQtyStatus !== sort2) {
      setSort2(urlQtyStatus);
      hasChanges = true;
    }

    if (urlReadyStatus && urlReadyStatus !== sort3) {
      setSort3(urlReadyStatus);
      hasChanges = true;
    }

    if (urlPage && parseInt(urlPage) !== pageNumber) {
      setPageNumber(parseInt(urlPage));
      hasChanges = true;
    }

    if (urlListView && (urlListView === 'true') !== listView) {
      setListView(urlListView === 'true');
      hasChanges = true;
    }

    // Always fetch data on initial load to ensure filters are applied
    if (!hasChanges) {
      getList();
    }
  }, []);

  useEffect(() => {
    if (search) {
      setPageNumber(1);
      getList({ page_number: 1 });
    }
  }, [search]);

  // Effect for list view toggle
  useEffect(() => {
    // Update URL when list view changes
    updateUrlParams({
      search: searchTerm,
      priority: sort1 !== 'Priority' ? sort1 : '',
      qtyStatus: sort2 !== 'Qty Status' ? sort2 : '',
      readyStatus: sort3 !== 'Ready Status' ? sort3 : '',
      page: pageNumber,
      listView: listView,
    });
  }, [listView]);

  // Effects for sorting changes
  useEffect(() => {
    const initialSort1 = searchParams.get('priority') || 'Priority';
    const initialSort2 = searchParams.get('qtyStatus') || 'Qty Status';
    const initialSort3 = searchParams.get('readyStatus') || 'Ready Status';

    // Only trigger a new API call if any of the values have actually changed
    if (sort1 !== initialSort1 || sort2 !== initialSort2 || sort3 !== initialSort3) {
      setPageNumber(1);
      getList({ page_number: 1 });
    }
  }, [sort1, sort2, sort3]);

  useEffect(() => {
    // Only fetch when pageNumber changes and it's not the initial load
    const currentUrlPage = searchParams.get('page');
    if (currentUrlPage !== pageNumber.toString() && pageNumber !== 0) {
      getList();
    }
  }, [pageNumber]);

  useEffect(() => {
    setSortedData(mfgData);
  }, [mfgData]);

  useEffect(() => {
    if (!searchTerm && search) {
      setPageNumber(1);
      setSearch(false);
      getList({ page_number: 1 });
    }
  }, [searchTerm]);

  const resetFilter = () => {
    setSort1('Priority');
    setSort2('Qty Status');
    setSort3('Ready Status');
    setPageNumber(1);

    // Update URL params - remove filters but keep search and listView
    updateUrlParams({
      search: searchTerm,
      page: '1',
      listView: listView,
    });

    getList({ page_number: 1 });
  };

  const handleJumpToPage = () => {
    const totalPages = Math.ceil(paginationData.total_records / 20);
    const targetPage = parseInt(jumpPage, 10);

    if (targetPage >= 1 && targetPage <= totalPages) {
      setPageNumber(targetPage);
    } else {
      popup(POPUP_TYPE.ERR, `Please enter a valid page number between 1 and ${totalPages}.`);
    }
  };

  return (
    <>
      <TitleBar
        title={[{ text: TITLES.MANUFACTURING, route: ROUTES.MANUFACTURING }]}
        listView={listView}
        setListView={setListView}
      />
      {!listView && <StatusBox status={MFG_STATUS} />}
      <div className="d-flex flex-wrap align-items-start justify-content-start gap-3 w-100">
        <div className="flex-grow-2">
          <SearchBar
            search={search}
            setSearchTerm={setSearchTerm}
            searchTerm={searchTerm}
            setSearch={setSearch}
            onDefaultSearch={getList}
          />
        </div>
        <div className=" d-flex flex-wrap align-items-center justify-content-start gap-2 mb-3">
          <DropDown dropdownMenu={sortingArray1} value={sort1} setValue={setSort1} />
          <DropDown dropdownMenu={sortingArray2} value={sort2} setValue={setSort2} />
          <DropDown dropdownMenu={sortingArray3} value={sort3} setValue={setSort3} />
          {(sort1 !== 'Priority' || sort2 !== 'Qty Status' || sort3 !== 'Ready Status') && (
            <Button
              clickHandler={resetFilter}
              className="blue-btn d-flex align-items-center justify-content-center px-3">
              {BUTTON.CLEAR_FILTERS}
            </Button>
          )}
        </div>
      </div>

      {!listView && mfgData.length > 0 && (
        <div className="pagination-container d-flex justify-content-between align-items-center mb-2">
          <h5 className="pagination-info">
            <span className="highlight">Records:</span>
            <span className="record-range">
              {paginationData.start} - {paginationData.end}
            </span>
            <span className="highlight">of</span>
            <span className="total-records">{paginationData.total_records}</span>
          </h5>

          <div className="pagination-controls d-flex align-items-center justify-content-end">
            <Button
              className={`blue-btn d-flex align-items-center justify-content-center px-3 me-2 ${paginationData.page === 1 ? 'disabled' : ''
                }`}
              clickHandler={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
              disabled={paginationData.page === 1}>
              Previous
            </Button>
            <div className="page-jump-container d-flex align-items-center my-2">
              <input
                type="number"
                className="page-jump-input form-control me-2"
                placeholder={`On page ${paginationData.page} of ${Math.ceil(paginationData.total_records / 20)}`}
                value={jumpPage}
                disabled={Math.ceil(paginationData.total_records / 20) === 1}
                readOnly={Math.ceil(paginationData.total_records / 20) === 1}
                onWheel={(e) => e.target.blur()}
                onChange={(e) => {
                  const value = e.target.value;
                  if (
                    value === '' ||
                    (/^[0-9]+$/.test(value) &&
                      value > 0 &&
                      value <= Math.ceil(paginationData.total_records / 20))
                  ) {
                    setJumpPage(value);
                  } else {
                    setJumpPage('');
                  }
                }}
              />
              <Button
                className={`blue-btn ${Math.ceil(paginationData.total_records / 20) === 1 ||
                  jumpPage === '' ||
                  paginationData.page == jumpPage
                  ? 'disabled'
                  : ''
                  } px-3`}
                clickHandler={() => {
                  handleJumpToPage();
                  setJumpPage('');
                }}
                disabled={
                  Math.ceil(paginationData.total_records / 20) === 1 ||
                  jumpPage === '' ||
                  paginationData.page == jumpPage
                }>
                Go
              </Button>
            </div>

            <Button
              className={`blue-btn d-flex align-items-center justify-content-center px-3 me-2 mx-1 ${paginationData.page === Math.ceil(paginationData.total_records / 20)
                ? 'disabled'
                : ''
                }`}
              clickHandler={() =>
                setPageNumber((prev) =>
                  Math.min(prev + 1, Math.ceil(paginationData.total_records / 20)),
                )
              }
              disabled={paginationData.page === Math.ceil(paginationData.total_records / 20)}>
              Next
            </Button>
          </div>
        </div>
      )}
      {!listView && (
        <div className="bg-white py-4 px-3 matrix-box mfg-tile-view">
          {isLoading ? (
            <div className="text-center my-2">
              <span className="spinner-border spinner-border-sm text-primary" role="status" />
              <span className="ms-2">Loading...</span>
            </div>
          ) : Array.isArray(sortedData) && sortedData.length > 0 ? (
            <div className="row custom-row">
              {sortedData.map((lot) =>
                lot.ready_qty === 0 ? (
                  <RedTile key={lot.id} lot={lot} />
                ) : lot.ready_qty < lot.total_qty ? (
                  <YellowTile key={lot.id} lot={lot} />
                ) : (
                  <GreenTile key={lot.id} lot={lot} />
                )
              )}
            </div>
          ) : (
            <div>
              <h4 className="px-1 text-center py-5">No Records Found</h4>
            </div>
          )}
        </div>
      )}
      {listView && (
        <PaginatedTable
          columns={MANUFACTURING_ESUN_LIST_COLUMN}
          initialData={sortedData}
          colFilter={false}
          to={ROUTES.MANUFACTURING}
          pageNumber={pageNumber}
          setPageNumber={setPageNumber}
          paginationData={paginationData}
          isLoading={isLoading}
        />
      )}
    </>
  );
};

export const getReadyQty = (lot) => {
  const qty = lot?.batch?.reduce((acc, cum) => {
    cum.state === DATA_FIELD.PACKED ? (acc = acc + cum.order_qty) : (acc = acc + 0);
    return acc;
  }, 0);
  return qty;
};

export const getBatchQty = (lot) => {
  const qty = reducedCalc(lot?.batch, DATA_FIELD.ORDER_QTY);
  return qty;
};
